<template>
  <v-dialog :value="openDialog" scrollable width="810px" @input="$emit('input', $event)" @click:outside="closeDialog">
    <v-card v-if="product" class="product-edit" height="640px">
      <v-card-title class="px-8">
        <span class="section-title">{{ 'Editar produto' }}</span> <br />
        <p class="subtitle pt-1" style="word-break: break-word">
          {{ 'Edições feitas serão aplicadas em todos os lugares que consomem produtos, como por exemplo seu frente de caixa e aplicativo.' }}
        </p>
      </v-card-title>
      <v-card-text class="card-content px-8">
        <v-container fluid class="pa-0">
          <v-row no-gutters justify="end">
            <v-col cols="auto">
              <a @click="$router.push({ path: `/produtos/alterar/${product.id}` })"> Ver detalhes do produto</a>
            </v-col>
          </v-row>
          <v-row class="pa-0">
            <v-col cols="5">
              <div class="d-flex align-center">
                <mf-upload-image
                  v-model="product.image"
                  file-name="product"
                  label-formats-accepted="PNG, JPG, JPEG ou WEBP."
                  :validation="['image/png', 'image/jpg', 'image/jpeg', 'image/webp']"
                  @upload="saveImage"
                />
              </div>
            </v-col>
            <v-col cols="7">
              <v-row>
                <v-col cols="4" class="pb-0 pt-0">
                  <v-text-field
                    v-model="product.legacySku"
                    disabled
                    label="SKU"
                    outlined
                    height="45px"
                    data-cy="productSkuField"
                    @blur="validateSku"
                    @focus="newSKUError = ''"
                  />
                </v-col>
                <v-col cols="8" class="pb-0 pt-0">
                  <v-text-field
                    v-model="product.name"
                    label="Nome"
                    height="45px"
                    :error-messages="nameErrorMessage"
                    outlined
                    data-cy="productDescriptionField"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" class="mt-n5">
                  <search-categories v-model="product" ignore-mounted :validation="$v" />
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-container>
        <mf-action-buttons
          :primary-button="{
            text: 'Salvar',
            action: submit,
            isVisible: true,
            isLoading: loading || loadingImageUpload,
            isDisabled: validatingSKU || validatingEAN,
          }"
          :extra-button="{
            text: 'Cancelar',
            action: closeDialog || loadingImageUpload,
            isVisible: true,
          }"
        />
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import orderBy from 'lodash/orderBy'
import { required, minLength, maxLength } from 'vuelidate/lib/validators'
import { notOnlySpace } from '@/helpers/validators.js'
import { MUTATION_UPDATE_PRODUCT, PRODUCT, MUTATION_UPLOAD_IMAGE } from '../../../graphql'
import { VMoney } from 'v-money'
import SearchCategories from './SearchCategories.vue'

export default {
  directives: { money: VMoney },
  components: {
    SearchCategories,
  },
  provide: function () {
    return {
      userHasPermission: this.userHasPermission,
    }
  },
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    productData: {
      type: Object,
      default: () => {},
    },
    categories: Array,
    categoriesTypes: Array,
    isNew: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    file: undefined,
    filePreview: null,
    product: {},
    localEans: [],
    loading: false,
    orderBy: orderBy,
    basisWeightKg: '',
    newEan: '',
    showAllEans: false,
    validatingEAN: false,
    validatingSKU: false,
    newSKUError: '',
    newEANError: '',
    kgFormat: {
      decimal: ',',
      thousands: '.',
      precision: 2,
      masked: false,
    },
    errorWeight: false,
    categoriesWithDepartment: [],
    subCategoriesWithCategory: [],
    loadingImageUpload: false,
  }),
  computed: {
    userHasPermission() {
      if (this.$getLoggedUser().has_club || (this.$getIsEcommerce() && this.userPermissions) || this.$getLoggedUser().has_mercapromo) {
        return true
      }

      return false
    },
    openDialog: {
      get() {
        return this.dialog
      },
      set(value) {
        this.$emit('update:dialog', value)
      },
    },
    nameErrorMessage() {
      if (this.$v.product.name.$error && !this.$v.product.name.required) return 'Campo obrigatório'
      if (this.$v.product.name.$error && !this.$v.product.name.notOnlySpace) return 'Não é permitido salvar apenas espaços'
      if (this.$v.product.name.$error && !this.$v.product.name.minLength) return 'O campo deve ter no mínimo 3 letras'
      if (this.$v.product.name.$error && !this.$v.product.name.maxLength) return 'O campo deve ter no máximo 100 letras'
      return ''
    },
    categoryErrorMessage() {
      if (!this.showCategoriesValidation) return ''
      if (this.$v.product?.categoryId?.$error) return 'É necessário selecionar todos os níveis de categorias para avançar.'
      return ''
    },
    subcategoryErrorMessage() {
      if (!this.thirdCategoryRequired) return ''
      if (this.$v.product?.categories_final_consumer?.sub_category?.$error) return 'É necessário selecionar todos os níveis de categorias para avançar.'
      return ''
    },
    eansErrorMessage() {
      if (this.newEANError) return this.newEANError
      if (this.$v.localEans.$error && !this.$v.localEans.required) return 'Campo obrigatório'
      return ''
    },
    basisWeightErrorMessage() {
      if (this.product.type === 'WEIGHT' && this.errorWeight) {
        return 'Campo obrigatório'
      }
      return ''
    },
    showCategoriesValidation() {
      return !!this.product?.departmentId
    },
    thirdCategoryRequired() {
      return !!this.product?.categoryId
    },
  },
  watch: {
    'product.image'(value) {
      if (!value) {
        this.product.images[0].url = ''
        this.product.images[0].urlThumbnail = ''
        this.product.images[0].urlMiniature = ''
      }
    },
  },
  created() {
    this.openDialog = true
    this.setData()
  },
  methods: {
    setData() {
      this.$v.$reset()
      this.newEANError = ''
      this.newSKUError = ''
      this.newEan = ''
      this.product = {
        ...this.productData,
        images: this.productData.images?.length > 0 ? this.productData.images : [{ url: '', urlThumbnail: '', urlMiniature: '' }],
        image: this.productData.images[0]?.url,
        departmentId: this.productData?.departmentId,
        categoryId: this.productData?.categoryId,
        sub_category: this.productData?.subcategoryId,
      }
      this.showAllEans = false
      this.errorWeight = false
    },
    async validateSku() {
      if (!this.product.sku) return

      try {
        this.validatingSKU = true
        const {
          data: { product },
        } = await this.$apollo.query({
          query: PRODUCT,
          variables: { sku: this.product.sku },
          fetchPolicy: 'network-only',
        })
        if (product) {
          this.newSKUError = 'SKU já cadastrada'
        } else {
          this.newSKUError = ''
        }
      } catch (e) {
        console.log(e)
        this.$snackbar({ message: 'Falha ao validar o SKU no servidor.' })
      }
      this.validatingSKU = false
    },
    closeDialog() {
      this.openDialog = false
    },
    updateProduct() {
      this.loading = true
      const product = {
        id: this.product.id,
        legacySku: this.product.legacySku,
        images: this.product.images[0]?.url ? this.product.images : [],
        departmentId: this.product?.departmentId || null,
        categoryId: this.product.categoryId || null,
        subcategoryId: this.product.subcategoryId || null,
        name: this.product.name,
        measure: this.product.measure,
      }
      product.images.forEach((image) => {
        delete image.__typename
      })
      delete product.measure?.__typename
      this.$apollo
        .mutate({
          mutation: MUTATION_UPDATE_PRODUCT,
          variables: {
            data: product,
          },
          context: {
            uri: this.$microservicesUrls['fedGateway'],
          },
          fetchPolicy: 'no-cache',
        })
        .then(() => {
          this.$snackbar({ message: 'Produto editado com sucesso.', snackbarColor: 'success' })
          this.$emit('updated')
          this.loading = false
          this.closeDialog()
        })
        .catch((error) => {
          if (error.message === 'GraphQL error: Product With Same Ean Already Exist Error') {
            this.$snackbar({ message: 'Já existe um produto com um dos eans criados.', snackbarColor: 'error' })

            return
          }

          this.$snackbar({ message: 'Erro ao editar produto.', snackbarColor: 'error' })
        })
    },
    async saveImage({ file }) {
      this.loadingImageUpload = true
      await this.$apollo
        .mutate({
          mutation: MUTATION_UPLOAD_IMAGE,
          variables: { file },
          context: {
            uri: this.$microservicesUrls['products'],
            headers: {
              'x-apollo-operation-name': 'UploadFile',
            },
          },
        })
        .then(({ data }) => {
          this.$snackbar({ message: 'Upload realizado com sucesso.' })
          this.product.images[0].url = data.uploadFile.url
          this.product.images[0].urlThumbnail = data.uploadFile.urlThumb
          this.product.images[0].urlMiniature = data.uploadFile.urlMini
          this.product.images[0].isMain = true
          this.loadingImageUpload = false
        })
        .catch((error) => {
          console.log(error)
          this.loadingImageUpload = false
          this.$snackbar({ message: 'Erro ao subir imagem.', snackbarColor: 'error' })
        })
    },
    async submit() {
      this.$v.$touch()
      if (this.$v.$error) {
        return this.$snackbar({ message: 'Preencha todos os campos corretamente', snackbarColor: 'error' })
      }

      try {
        this.loading = true

        await this.updateProduct()

        if (this.isNew) this.$snackbar({ message: 'Produto criado com sucesso!', snackbarColor: 'success' })
        else this.$snackbar({ message: 'Produto atualizado com sucesso!', snackbarColor: 'success' })
      } catch (error) {
        console.log(error)
        if (this.isNew) this.$snackbar({ message: 'Erro ao criar produto', snackbarColor: 'error' })
        else this.$snackbar({ message: 'Erro ao editar produto', snackbarColor: 'error' })
      }
    },
    clearCategories() {
      this.product.departmentId = ''
      this.product.categoryId = ''
      this.product.subcategoryId = ''
    },
  },
  validations() {
    const product = {
      name: {
        required,
        minValue: minLength(1),
        maxLength: maxLength(250),
        notOnlySpace,
      },
    }

    if (this.product.departmentId) {
      product.categoryId = {
        required,
      }
    }
    return { product }
  },
}
</script>

<style lang="scss" scoped>
.product-edit {
  .image-placeholder {
    background-color: $cinza-l90;
    width: 230px;
    height: 230px;
    &:hover {
      cursor: pointer;
    }
    border: solid 1px $cinza-l10;
    text-align: center;
    display: table;
  }
  .category-label {
    font-family: 'Lato';
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    line-height: 120%;
    color: $cinza-d10;
  }
  .centered-text {
    display: table-cell;
    vertical-align: middle;
  }
  .product-title {
    font-size: 16px;
    font-weight: bold;
    margin-bottom: 0;
    color: $cinza-d10;
  }
  .product-data {
    font-size: 16px;
    opacity: 0.87;
    margin-bottom: 0;
  }
  .input-file {
    display: none;
  }
  .hint-text {
    font-size: 12px;
    letter-spacing: 0.23px;
    color: $cinza-0;
  }
  .sub-text {
    font-size: 12px;
    letter-spacing: 0.23px;
  }
  .sub-text-disabled {
    font-size: 12px;
    letter-spacing: 0.23px;
    color: rgba(0, 0, 0, 0.38);
  }

  .clear-categories {
    color: $vermelho-0;
    &:hover {
      text-decoration: underline;
    }
    cursor: pointer;
  }
}
::v-deep .v-input__slot {
  min-height: 45px !important;
}
::v-deep .v-input__slot .v-text-field__slot .v-label {
  top: 14px !important;
}
::v-deep .v-input__slot .v-select__slot .v-label {
  top: 14px !important;
}
::v-deep .theme--light.v-text-field--outlined:not(.v-input--is-focused).v-input--is-disabled > .v-input__control > .v-input__slot fieldset {
  background: $cinza-l90 !important;
}
::v-deep .v-input__slot .v-select__slot .mf-icon {
  bottom: 4px !important;
}
</style>
